import { useTranslation } from 'react-i18next'

import { Badge, Card, Overline, Section, SectionTitle } from '@/design-system/primitives'
import { formatTime } from '@/lib/datetime'
import { cn } from '@/lib/cn'

import type { PublicCakeMoment, PublicProgramItem, ProgramItemStatus } from './types'

const STATUS_TONES: Record<ProgramItemStatus, string> = {
  scheduled: 'border-line-subtle',
  running: 'border-ember-400 shadow-ember',
  done: 'border-line-subtle opacity-60',
  skipped: 'border-line-subtle opacity-40',
}

/**
 * Programme public — RULE 10 : lisible par tout le monde, sans billet.
 *
 * Le programme est un argument de vente autant qu'une information pratique :
 * quelqu'un qui hésite doit voir exactement ce qui l'attend.
 */
export function ProgramSection({
  items,
  timezone,
  locale,
}: {
  items: PublicProgramItem[]
  timezone: string
  locale: string
}) {
  const { t } = useTranslation()

  if (items.length === 0) return null

  return (
    <Section id="programme">
      <Overline>{t('program.overline')}</Overline>
      <SectionTitle>{t('program.title')}</SectionTitle>

      <ol className="mt-6 space-y-3">
        {items.map((item) => (
          <li key={item.id}>
            <Card className={cn('border', STATUS_TONES[item.status])}>
              <div className="flex flex-wrap items-baseline gap-x-6 gap-y-2">
                {/* L'heure garde une largeur fixe : la colonne reste alignée
                    même quand une étape n'en a pas. */}
                <span className="w-20 shrink-0 font-mono text-sm tabular-nums text-champagne-300">
                  {item.starts_at ? formatTime(item.starts_at, locale, timezone) : '—'}
                </span>

                <div className="min-w-0 flex-1">
                  <div className="flex flex-wrap items-center gap-3">
                    <h3 className="font-display text-heading-sm text-content-primary">
                      {item.title}
                    </h3>
                    {item.status === 'running' ? (
                      <Badge tone="ember">{t('program.running')}</Badge>
                    ) : null}
                    {item.status === 'skipped' ? (
                      <Badge tone="neutral">{t('program.skipped')}</Badge>
                    ) : null}
                  </div>

                  {item.description ? (
                    <p className="mt-2 text-body-lg text-content-secondary">{item.description}</p>
                  ) : null}

                  {item.host || item.location ? (
                    <p className="mt-2 text-caption text-content-muted">
                      {[item.host, item.location].filter(Boolean).join(' · ')}
                    </p>
                  ) : null}
                </div>
              </div>
            </Card>
          </li>
        ))}
      </ol>

    </Section>
  )
}

/**
 * 🎂 Le LeYAMO Cake Moment — RULE 12.
 *
 * Sa propre section, pas une carte au bas d'une liste : c'est le moment où
 * toute la salle fait la même chose en même temps. Et il n'est vendable nulle
 * part — aucun catalogue ne le référence, par construction.
 */
export function CakeMomentSection({ cake }: { cake: PublicCakeMoment | null }) {
  const { t } = useTranslation()
  if (!cake) return null

  return (
    <Section id="cake">
      <div className="hairline-champagne overflow-hidden rounded-lg">
        <div className="grid gap-0 md:grid-cols-2">
          {cake.media_url ? (
            <img
              src={cake.media_url}
              alt=""
              className="h-full max-h-96 w-full object-cover"
              loading="lazy"
            />
          ) : null}

          <div className="p-8 sm:p-12">
            <p className="text-5xl leading-none">🎂</p>
            <Overline className="mt-6">{t('cake.overline')}</Overline>
            <h2 className="mt-3 font-display text-heading-lg text-champagne-300">{cake.name}</h2>

            {cake.message ? (
              <p className="mt-5 text-body-lg italic text-content-primary">{cake.message}</p>
            ) : null}
            {cake.description ? (
              <p className="mt-4 text-body-lg text-content-secondary">{cake.description}</p>
            ) : null}

            {/* La mention qui évite la question « combien ça coûte ? ». */}
            <p className="mt-6 text-caption text-content-muted">{t('cake.shared')}</p>
          </div>
        </div>
      </div>
    </Section>
  )
}
